import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../../useSEO';
import { useSite } from '../../components/StoreLayout';
import { STORE_BASE } from '../../routeBase';

const lbl = { fontSize: 12.5, fontWeight: 600, color: 'var(--sf-primary)', display: 'block', marginBottom: 5 };
const inp = { width: '100%', padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };

function round2(n) { return Math.round((n + Number.EPSILON) * 100) / 100; }

export default function RetailMarginCalculator() {
  const { content: c } = useSite();
  useSEO({
    title: c['tools.retail-margin.meta_title'] || 'Retail Margin & Safe Discount Calculator for Opticians',
    description: c['tools.retail-margin.meta_description'] || 'Free retail margin calculator for optical retailers — find your minimum profitable selling price and see exactly how much discount you can safely offer before you lose money.',
    keywords: c['tools.retail-margin.meta_keywords'] || 'retail margin calculator, safe discount calculator, optical store pricing, minimum selling price, retail markup calculator, optician tools',
    canonical: `${window.location.origin}/store/tools/retail-margin-calculator`,
  });

  const [cost, setCost] = useState('40');
  const [overhead, setOverhead] = useState('25');
  const [mrp, setMrp] = useState('90');
  const [discount, setDiscount] = useState(10);

  const result = useMemo(() => {
    const c = parseFloat(cost);
    const oh = Math.max(0, parseFloat(overhead) || 0);
    const price = parseFloat(mrp);
    if (!c || c <= 0) return { error: 'Enter a valid landed cost' };
    if (!price || price <= 0) return { error: 'Enter your current retail price / MRP' };

    const trueCost = round2(c * (1 + oh / 100));
    const marginAtMrpPct = round2(((price - trueCost) / price) * 100);
    const breakEvenDiscountPct = price > 0 ? round2((1 - trueCost / price) * 100) : 0;

    const profitAtMrp = round2(price - trueCost);
    const discPrice = round2(price * (1 - discount / 100));
    const profit = round2(discPrice - trueCost);
    const marginPct = discPrice > 0 ? round2((profit / discPrice) * 100) : 0;
    const status = profit <= 0 ? 'loss' : marginPct < 10 ? 'thin' : 'safe';

    return { trueCost, marginAtMrpPct, breakEvenDiscountPct, profitAtMrp, discPrice, profit, marginPct, status };
  }, [cost, overhead, mrp, discount]);

  const card = { background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 24 };
  const statusColors = {
    safe: { bg: '#f0fdf4', fg: '#16a34a', label: 'Still profitable' },
    thin: { bg: '#fffbeb', fg: '#b45309', label: 'Thin margin — proceed carefully' },
    loss: { bg: '#fef2f2', fg: '#b91c1c', label: "You'd be selling at a loss" },
  };

  return (
    <div style={{ background: 'var(--sf-body-bg)' }}>
      {/* HERO */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '48px 24px 16px', textAlign: 'center' }}>
        <Link to={`${STORE_BASE}/tools`} style={{ fontSize: 12.5, color: 'var(--sf-hover)', fontWeight: 600, textDecoration: 'none' }}>
          <i className="ti ti-arrow-left" style={{ fontSize: 13, verticalAlign: -1 }} /> All free tools
        </Link>
        <h1 style={{ fontSize: 'clamp(24px,3.6vw,34px)', fontWeight: 800, color: 'var(--sf-primary)', margin: '14px 0 12px' }}>
          Retail Margin &amp; Safe Discount Calculator
        </h1>
        <p style={{ fontSize: 14.5, color: 'var(--sf-muted)', maxWidth: 620, margin: '0 auto', lineHeight: 1.7 }}>
          Know your break-even price before a customer starts haggling — and exactly how far you can go.
        </p>
      </section>

      <section style={{ maxWidth: 1100, margin: '24px auto 0', padding: '0 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px,1fr) minmax(300px,1.1fr)', gap: 24 }} className="rmc-grid">
          {/* INPUTS */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--sf-primary)', margin: '0 0 16px' }}>Cost &amp; Pricing</h3>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Landed Cost per Piece</label>
              <input type="number" min="0" step="0.01" value={cost} onChange={e => setCost(e.target.value)} style={inp} />
              <div style={{ fontSize: 11, color: 'var(--sf-muted)', marginTop: 4 }}>
                What it actually cost you to get one piece into the shop — use our Landed Cost Estimator if you need this figure.
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Overhead (%)</label>
              <input type="number" min="0" step="1" value={overhead} onChange={e => setOverhead(e.target.value)} style={inp} />
              <div style={{ fontSize: 11, color: 'var(--sf-muted)', marginTop: 4 }}>
                Rent, staff, utilities — your rough running cost as a % of product cost.
              </div>
            </div>
            <div style={{ marginBottom: 6 }}>
              <label style={lbl}>Your Retail Price / MRP</label>
              <input type="number" min="0" step="0.01" value={mrp} onChange={e => setMrp(e.target.value)} style={inp} />
            </div>
          </div>

          {/* RESULTS */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--sf-primary)', margin: '0 0 16px' }}>Margin</h3>
            {result.error && (
              <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 13, padding: '10px 12px', borderRadius: 8, marginBottom: 14 }}>
                {result.error}
              </div>
            )}
            {!result.error && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
                  <Stat label="Minimum Profitable Price" value={result.trueCost} highlight />
                  <Stat label="Margin at Full Price" value={`${result.marginAtMrpPct}%`} />
                  <Stat label="Profit at Full Price" value={result.profitAtMrp} />
                  <Stat label="Max Safe Discount" value={`${Math.max(0, result.breakEvenDiscountPct)}%`} />
                </div>

                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--sf-hover)', marginBottom: 8 }}>
                  Safe Discount Slider
                </div>
                <input
                  type="range" min="0" max="30" step="1" value={discount}
                  onChange={e => setDiscount(Number(e.target.value))}
                  style={{ width: '100%', marginBottom: 6 }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--sf-muted)', marginBottom: 16 }}>
                  <span>0%</span><span style={{ fontWeight: 700, color: 'var(--sf-primary)' }}>{discount}% off</span><span>30%</span>
                </div>

                <div style={{ background: statusColors[result.status].bg, borderRadius: 10, padding: '14px 16px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: statusColors[result.status].fg, marginBottom: 8 }}>
                    {statusColors[result.status].label}
                  </div>
                  <Row label="Discounted Price" value={result.discPrice} />
                  <Row label="Profit per Piece" value={result.profit} />
                  <Row label="Margin at This Discount" value={`${result.marginPct}%`} />
                </div>

                <p style={{ fontSize: 11, color: 'var(--sf-muted)', lineHeight: 1.6, marginTop: 16 }}>
                  "Minimum Profitable Price" already includes your overhead — sell below it and you're losing money
                  on the piece even before counting anything else. Figures are per-piece, in whatever currency you entered.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section style={{ background: 'var(--sf-primary)', color: '#fff', marginTop: 48 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px', display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 6px' }}>Stock up your shop for less</h3>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)', margin: 0, maxWidth: 460 }}>
              View our latest wholesale frame collections — acetate, TR90 and metal, shipped worldwide.
            </p>
          </div>
          <Link to={`${STORE_BASE}/shop`} style={{
            background: '#fff', color: 'var(--sf-primary)', padding: '11px 22px', borderRadius: 8,
            fontWeight: 700, fontSize: 13, textDecoration: 'none', flexShrink: 0,
          }}>
            Browse Wholesale Catalog
          </Link>
        </div>
      </section>

      <style>{`
        @media (max-width: 780px) {
          .rmc-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div style={{
      background: highlight ? 'var(--sf-primary)' : '#f7f8fa', color: highlight ? '#fff' : 'var(--sf-primary)',
      borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700, opacity: highlight ? 0.8 : 0.6 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, marginTop: 3 }}>{value}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0', fontSize: 13 }}>
      <span style={{ color: 'var(--sf-muted)' }}>{label}</span>
      <span style={{ fontWeight: 700 }}>{value}</span>
    </div>
  );
}
